import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  FileCheck, ArrowRight, CheckCircle2, Clock, Globe, Shield, 
  FileText, AlertCircle, Phone, Mail, Scale, Users, X, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';

const CustomsPage = () => {
  const { t } = useTranslation();

  // Structured data for Customs service
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Customs Clearance Coordination",
    "description": "Import and export customs clearance coordinated through licensed customs agents.",
    "provider": {
      "@type": "Organization",
      "name": "Elementary Cargo B.V.",
      "url": "https://elementarycargo.com"
    },
    "serviceType": "Customs Clearance",
    "areaServed": "European Union"
  };

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Customs", path: "/services/customs" }
  ];

  const features = [
    { 
      icon: Users, 
      title: t('customsPage.features.singleContact'), 
      description: t('customsPage.features.singleContactDesc')
    },
    { 
      icon: Scale, 
      title: t('customsPage.features.agentNetwork'), 
      description: t('customsPage.features.agentNetworkDesc')
    },
    { 
      icon: Shield, 
      title: t('customsPage.features.documented'), 
      description: t('customsPage.features.documentedDesc')
    },
    { 
      icon: FileText, 
      title: t('customsPage.features.docSupport'), 
      description: t('customsPage.features.docSupportDesc')
    }
  ];

  const services = [
    {
      title: t('customsPage.services.import'),
      description: t('customsPage.services.importDesc'),
      features: t('customsPage.services.importFeatures', { returnObjects: true })
    },
    {
      title: t('customsPage.services.export'),
      description: t('customsPage.services.exportDesc'),
      features: t('customsPage.services.exportFeatures', { returnObjects: true })
    },
    {
      title: t('customsPage.services.transit'),
      description: t('customsPage.services.transitDesc'),
      features: t('customsPage.services.transitFeatures', { returnObjects: true })
    },
    {
      title: t('customsPage.services.compliance'),
      description: t('customsPage.services.complianceDesc'),
      features: t('customsPage.services.complianceFeatures', { returnObjects: true })
    }
  ];

  const documentTypes = t('customsPage.documentTypes', { returnObjects: true });
  const whatWeDo = t('customsPage.whatWeDoList', { returnObjects: true });
  const whatWeDontDo = t('customsPage.whatWeDoNotList', { returnObjects: true });

  return (
    <div className="min-h-screen bg-offwhite" data-testid="customs-page">
      <SEO 
        titleKey="seo.customs.title"
        descriptionKey="seo.customs.description"
        path="/services/customs"
        structuredData={serviceSchema}
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy-900 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1449247666642-264389f5f5b1?w=1920&q=80"
            alt="Customs documentation - coordinated through external agents"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900 via-navy-900/95 to-navy-900/80" />
        </div>
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-gold/20 rounded-xl flex items-center justify-center">
                <FileCheck className="w-7 h-7 text-gold" />
              </div>
              <span className="text-gold text-sm font-semibold tracking-wider uppercase">{t('customsPage.badge')}</span>
            </div>
            <h1 className="font-space text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('customsPage.heroTitle')}
            </h1>
            <p className="text-platinum-light text-lg md:text-xl leading-relaxed mb-8">
              {t('customsPage.heroSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/quote" data-testid="customs-quote-btn">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all">
                  {t('customsPage.requestQuote')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-2 border-platinum/30 text-white hover:bg-white/10 px-8 py-4 rounded-lg">
                  {t('customsPage.talkWithUs')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('customsPage.whyCoordinateWithUs')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900">
              {t('customsPage.structuredCoordination')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 hover:shadow-lg transition-all">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 rounded-xl bg-navy-900/5 flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-7 h-7 text-gold" />
                    </div>
                    <h3 className="font-space font-semibold text-navy-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-500 text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('customsPage.whatWeCoordinate')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('customsPage.customsSupport')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('customsPage.customsSupportDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={`item-${index}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border border-platinum-light hover:border-gold/30 transition-colors">
                  <CardContent className="p-8">
                    <h3 className="font-space text-xl font-bold text-navy-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <ul className="space-y-2">
                      {Array.isArray(service.features) && service.features.map((feature) => (
                        <li key={`${service.title.slice(0, 10)}-${feature.slice(0, 15)}`} className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
                {t('customsPage.documentation')}
              </span>
              <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-6">
                {t('customsPage.documentsWeCoordinate')}
              </h2>
              <p className="text-platinum-light mb-8">
                {t('customsPage.documentsWeCoordinateDesc')}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Array.isArray(documentTypes) && documentTypes.map((doc, index) => (
                  <div key={`item-${index}`} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-gold flex-shrink-0" />
                    <span className="text-sm">{doc}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&q=80"
                  alt="Documentation coordinated through customs partners"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* EORI Info */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="w-16 h-16 rounded-2xl bg-navy-900/5 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-gold" />
            </div>
            <h2 className="font-space text-3xl font-bold text-navy-900 mb-4">
              {t('customsPage.registeredCompliant')}
            </h2>
            <p className="text-slate-600 mb-8">
              {t('customsPage.registeredCompliantDesc')}
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-slate-700">
              <div className="bg-offwhite px-6 py-3 rounded-xl">
                <p className="text-xs text-slate-500 mb-1">KVK Registration</p>
                <p className="font-semibold">42041124</p>
              </div>
              <div className="bg-offwhite px-6 py-3 rounded-xl">
                <p className="text-xs text-slate-500 mb-1">EORI Number</p>
                <p className="font-semibold">NL3785680245</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Transparency Section */}
      <section className="py-20 bg-offwhite">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-gold text-sm font-semibold tracking-wider uppercase mb-4 block">
              {t('customsPage.ourRole')}
            </span>
            <h2 className="font-space text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              {t('customsPage.clearAboutWhatWeDo')}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('customsPage.transparencyDesc')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* What We Do */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-green-200 bg-green-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('customsPage.whatWeDo')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDo) && whatWeDo.map((item, index) => (
                      <li key={`item-${index}`} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* What We Don't Do */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-2 border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-slate-400 flex items-center justify-center">
                      <X className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-space text-xl font-bold text-navy-900">{t('customsPage.whatWeDoNot')}</h3>
                  </div>
                  <ul className="space-y-4">
                    {Array.isArray(whatWeDontDo) && whatWeDontDo.map((item, index) => (
                      <li key={`item-${index}`} className="flex items-start gap-3">
                        <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className="font-space text-3xl md:text-4xl font-bold text-white mb-4">
              {t('customsPage.needCoordination')}
            </h2>
            <p className="text-platinum-light text-lg mb-8">
              {t('customsPage.needCoordinationDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/quote">
                <Button className="bg-gradient-to-r from-gold-light via-gold to-gold-dark text-navy-900 font-semibold px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl hover:shadow-gold/25 transition-all w-full sm:w-auto">
                  {t('customsPage.requestAQuote')} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 text-platinum-light text-sm">
              <a href="mailto:info@elementarycargo.com" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Mail className="w-4 h-4" /> info@elementarycargo.com
              </a>
              <a href="tel:+31625381824" className="flex items-center gap-2 hover:text-gold transition-colors">
                <Phone className="w-4 h-4" /> +31 62 538 1824
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CustomsPage;
